import React from 'react';
import { motion } from "framer-motion";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

const Pool = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@300;400&display=swap');

        .pool-wrap {
          background: #fff;
          min-height: 100vh;
          font-family: Arial, Helvetica, sans-serif;
          color: #111;
          -webkit-font-smoothing: antialiased;
          padding-top: 150px;
        }

        /* ── HERO ── */
        .pool-hero {
          position: relative;
          width: 90%;
          height: min(48vh, 420px);
          overflow: hidden;
          background: #0d1a2e;
          margin: 0 auto;
          border-radius: 20px;
        }
        .pool-hero img {
          width: 100%; height: 100%;
          object-fit: cover; object-position: center 40%;
          filter: brightness(0.55) saturate(0.75);
          display: block;
          transform: scale(1.04);
          transition: transform 8s ease;
          border-radius: 20px;
        }
        .pool-hero:hover img { transform: scale(1); }
        .pool-hero::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 50%, rgba(255,255,255,0.5) 100%);
        }

        .pool-hero-badge {
          position: absolute; top: clamp(1rem,3vw,2rem); right: clamp(1.5rem,5vw,3rem);
          z-index: 2;
          border: 1px solid rgba(255,255,255,0.18);
          backdrop-filter: blur(8px);
          background: rgba(255,255,255,0.07);
          padding: 0.4rem 0.9rem; border-radius: 2px;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8px; letter-spacing: 0.24em; text-transform: uppercase;
          color: rgba(255,255,255,0.55);
        }

        .pool-hero-label {
          position: absolute; bottom: 0; left: 0; z-index: 2;
          padding: clamp(1.5rem, 5vw, 3rem) clamp(1.5rem, 7vw, 5rem);
        }
        .pool-hero-eyebrow {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9px; letter-spacing: 0.3em; text-transform: uppercase;
          color: rgba(255,255,255,0.5);
          margin-bottom: 0.6rem;
          display: flex; align-items: center; gap: 10px;
        }
        .pool-hero-eyebrow::before { content:''; width:24px; height:1px; background:rgba(255,255,255,0.35); }
        .pool-hero-title {
          font-family: Arial, Helvetica, sans-serif;
          font-size: clamp(2.8rem, 8vw, 6.5rem);
          font-weight: 700; line-height: 0.92; letter-spacing: -0.03em;
          color: #fff;
        }
        .pool-hero-title em { font-style: italic; font-weight: 400; color: rgba(255,255,255,0.6); }

        /* ── HERO mobile ── */
        @media (max-width: 600px) {
          .pool-wrap {
            padding-top: 0;
          }
          .pool-hero {
            width: 94%;
            height: 200px;
            min-height: unset;
            border-radius: 16px;
            margin: 120px auto 0;
          }
          .pool-hero img {
            border-radius: 16px;
            filter: brightness(0.50) saturate(0.85);
          }
          .pool-hero-label {
            padding: 0.85rem 1rem 1rem;
          }
          .pool-hero-eyebrow {
            font-size: 7.5px;
            letter-spacing: 0.16em;
            margin-bottom: 0.35rem;
            gap: 6px;
          }
          .pool-hero-eyebrow::before {
            width: 14px;
            height: 1px;
          }
          .pool-hero-title {
            font-size: clamp(1.5rem, 5vw, 2.5rem);
            line-height: 1.1;
          }
        }

        /* ── BODY ── */
        .pool-body {
          max-width: 1100px; margin: 0 auto;
          padding: clamp(1.2rem, 3vw, 2.5rem) clamp(1.5rem, 7vw, 5rem) clamp(4rem, 8vw, 7rem);
        }

        /* meta strip */
        .pool-meta {
          display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;
          padding-bottom: 1.5rem; border-bottom: 1px solid #eee; margin-bottom: 2.5rem;
        }
        .pool-meta-item { display: flex; flex-direction: column; gap: 3px; }
        .pool-meta-key {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8px; letter-spacing: 0.22em; text-transform: uppercase; color: #bbb;
        }
        .pool-meta-val { font-size: 0.85rem; font-weight: 500; color: #333; }
        .pool-meta-dot { width: 3px; height: 3px; border-radius: 50%; background: #e0e0e0; margin-top: 12px; }
        @media (max-width: 560px) { .pool-meta-dot { display: none; } }

        /* 2-col layout */
        .pool-content {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: clamp(3rem, 6vw, 6rem); align-items: start;
        }
        @media (max-width: 720px) { .pool-content { grid-template-columns: 1fr; gap: 2.5rem; } }

        /* left */
        .pool-lead-label {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8px; letter-spacing: 0.28em; text-transform: uppercase;
          color: #1d4ed8; display: flex; align-items: center; gap: 8px; margin-bottom: 1.2rem;
        }
        .pool-lead-label::before { content:''; width:14px; height:1px; background:#1d4ed8; }
        .pool-lead-heading {
          font-family: Arial, Helvetica, sans-serif;
          font-size: clamp(1.8rem, 4vw, 3rem); font-weight: 400;
          line-height: 1.1; letter-spacing: -0.025em; color: #111; margin-bottom: 1.8rem;
        }
        .pool-lead-heading em { font-style: italic; }
        .pool-lead-p {
          font-size: 1.05rem; line-height: 1.88; color: #444; font-weight: 300;
        }
        .pool-lead-p::first-letter {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 4.2em; font-weight: 700;
          float: left; line-height: 0.75; margin: 0.06em 0.1em 0 0; color: #111;
        }

        /* right */
        .pool-right { padding-top: 0.2rem; }
        .pool-body-p { font-size: 0.98rem; line-height: 1.88; color: #555; font-weight: 300; margin-bottom: 2rem; }

        /* highlight stat cards */
        .pool-stats {
          display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;
          margin-bottom: 2rem;
        }
        .pool-stat {
          padding: 1.1rem 1.2rem;
          border: 1px solid #eef2ff;
          background: #f5f8ff;
          border-radius: 6px;
        }
        .pool-stat-val {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 1.6rem; font-weight: 700; color: #1d4ed8;
          line-height: 1;
        }
        .pool-stat-label {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 7.5px; letter-spacing: 0.18em; text-transform: uppercase;
          color: #93aee0; margin-top: 4px;
        }

        /* pull quote */
        .pool-quote {
          border-left: 3px solid #1d4ed8; padding: 1.2rem 1.5rem;
          background: #f0f5ff;
        }
        .pool-quote-text {
          font-family: Arial, Helvetica, sans-serif;
          font-size: clamp(1rem, 2vw, 1.25rem); font-style: italic;
          font-weight: 400; line-height: 1.55; color: #222;
        }

        /* footer */
        .pool-foot {
          border-top: 1px solid #eee;
          padding: 1.5rem clamp(1.5rem, 7vw, 5rem) 2.5rem;
          max-width: 1100px; margin: 0 auto;
          display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.6rem;
        }
        .pool-foot-l { font-family: Arial, Helvetica, sans-serif; font-size: 8px; letter-spacing: 0.18em; text-transform: uppercase; color: #ccc; }
        .pool-foot-r { font-family: Arial, Helvetica, sans-serif; font-size: 0.8rem; font-style: italic; color: #ccc; }
      `}</style>

      <div className="pool-wrap">

        {/* HERO */}
        <motion.div className="pool-hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9 }}>
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2FPool.jpg?alt=media&token=a0f5e6ae-bc37-4905-8ab1-62063d189ae6"
            alt="The Pool of Life, BCS WHQs Calabar"
          />
          <div className="pool-hero-badge">Commissioned 2012</div>
          <motion.div className="pool-hero-label" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            {/* <p className="pool-hero-eyebrow">Brotherhood of the Cross and Star</p>
            <h1 className="pool-hero-title">Pool of <em>Life</em></h1> */}
          </motion.div>
        </motion.div>

        {/* BODY */}
        <div className="pool-body">

          {/* meta strip */}
          <motion.div className="pool-meta" {...fadeUp(0.3)}>
            <div className="pool-meta-item">
              <span className="pool-meta-key">Location</span>
              <span className="pool-meta-val">BCS WHQs, Calabar</span>
            </div>
            <div className="pool-meta-dot" />
            <div className="pool-meta-item">
              <span className="pool-meta-key">Built</span>
              <span className="pool-meta-val">2010 – 2012</span>
            </div>
            <div className="pool-meta-dot" />
            <div className="pool-meta-item">
              <span className="pool-meta-key">Commissioned By</span>
              <span className="pool-meta-val">Archbishop Archibong Archibong</span>
            </div>
            <div className="pool-meta-dot" />
            <div className="pool-meta-item">
              <span className="pool-meta-key">Purpose</span>
              <span className="pool-meta-val">Baptismal Pool</span>
            </div>
          </motion.div>

          {/* 2-col content */}
          <div className="pool-content">

            {/* LEFT */}
            <motion.div {...fadeUp(0.45)}>
              <p className="pool-lead-label">Origins</p>
              <h2 className="pool-lead-heading">A gift from<br /><em>Akwa Ibom</em> brethren</h2>
              <p className="pool-lead-p">
                The Pool of Life was constructed by the Akwa Ibom State Brethren as their
                appreciation for God's love in calling them into His Kingdom. Built between
                2010 and 2012, it was commissioned under the administration of His Grace
                Archbishop Archibong Archibong.
              </p>
            </motion.div>

            {/* RIGHT */}
            <motion.div className="pool-right" {...fadeUp(0.6)}>
              <p className="pool-body-p">
                The Pool came at a critical time when BCS leadership sought befitting waters
                to baptize the many personalities that thronged the WHQs in Calabar. Situated
                within the BCS WHQs premises, it is far more than a physical structure.
              </p>

              <div className="pool-stats">
                <div className="pool-stat">
                  <div className="pool-stat-val">2010</div>
                  <div className="pool-stat-label">Construction began</div>
                </div>
                <div className="pool-stat">
                  <div className="pool-stat-val">2012</div>
                  <div className="pool-stat-label">Commissioned</div>
                </div>
              </div>

              <div className="pool-quote">
                <p className="pool-quote-text">
                  "The Holy Father has assigned unique angels and spirits to cleanse, spiritually
                  and physically, the body and soul of anyone that takes a dip in these waters —
                  hence the name, The Pool of Life."
                </p>
              </div>
            </motion.div>

          </div>
        </div>

        {/* FOOTER */}
         <div className="pool-foot">
          <span className="pool-foot-l">Pool of Life · Brotherhood of the Cross and Star</span>
          <span className="pool-foot-r">BCS WHQs, Calabar · Est. 2012</span>
        </div> 

      </div>
    </>
  );
};

export default Pool;
